import React, { Dispatch, SetStateAction, useEffect } from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';

import { get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import SmsFailedRoundedIcon from '@mui/icons-material/SmsFailedRounded';
import { Box, Divider, Drawer, Stack, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import { RootState } from '../../../../app/rootReducer';
import { AppDispatch } from '../../../../app/store';
import {
  FlexCol,
  FlexGrid,
  FlexRow,
  NoraButton,
  Spacer,
} from '../../../../common/components/v2';
import { Decision } from '../../../../common/constants/decision';
import { Resource } from '../../../../common/constants/resource';
import ElementLocators from '../../../../common/ElementLocators';
import {
  getValidationResult,
  validateMIBCodes,
} from '../../decision/decisionSlice';
import { useDecisionSubmit } from '../../decision/useDecisionSubmit';
import { IDecision } from '../../interfaces/IDecision';
import { IDecisionRequest } from '../../interfaces/IDecisionRequest';
import { IDecisionUpdateRequest } from '../../interfaces/IDecisionUpdateRequest';
import DrawerHeader from '../DrawerHeader';
import { ReasonsSummary } from '../ReasonsSummary';
import styles from '../styles.module.scss';
import { PostponeFormData } from './usePostponeForm';

const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export const ReviewPostpone = ({
  applicationId,
  isOpen,
  setIsOpen,
  coverage,
  term,
  rateClass,
  tobaccoClass,
  editedDecision,
  formData,
  onBack,
  isEditLeadingToCLR,
}: {
  applicationId: string;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  coverage: number;
  term: number;
  rateClass: string;
  tobaccoClass: string;
  editedDecision?: IDecision;
  formData: PostponeFormData;
  onBack: () => void;
  isEditLeadingToCLR?: boolean;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { submitCreate, submitEdit } = useDecisionSubmit(applicationId, 'postpone');

  const codeback = useSelector(state => state.decision);
  let resultMap: Map<string, object> = new Map();

  useEffect(() => {
    if (!formData.mibCodes?.length) return;
    resultMap = new Map();
    dispatch(validateMIBCodes(applicationId, formData.mibCodes));
  }, [formData.mibCodes]);

  return (
    <Drawer
      open={isOpen}
      anchor="right"
      variant="persistent"
      classes={{ paper: styles.Drawer }}
      transitionDuration={{ enter: 0, exit: 0 }}
    >
      <DrawerHeader
        title={Resource.POSTPONE_APPLICATION_HEADER}
        onBack={onBack}
      />
      <Divider />
      <Box m={3}>
        <FlexRow>
          <FlexCol xs={12}>
            <Typography variant="caption">Applied for</Typography>
          </FlexCol>
        </FlexRow>
        <Stack mt={2} direction="row" spacing={4}>
          <Stack spacing={1} sx={{ minWidth: 150 }}>
            <Typography variant="body2">Max Coverage</Typography>
            <Typography variant="body2">Term Length</Typography>
            <Typography variant="body2">Class</Typography>
            <Typography variant="body2">Tobacco</Typography>
          </Stack>
          <Stack spacing={1}>
            <Typography variant="body1">{`$${coverage.toLocaleString()}`}</Typography>
            <Typography variant="body1">{term}</Typography>
            <Typography variant="body1">{rateClass}</Typography>
            <Typography variant="body1">{tobaccoClass}</Typography>
          </Stack>
        </Stack>
      </Box>
      <Divider />
      <Box m={3}>
        <FlexGrid>
          {!!editedDecision && (
            <>
              <Spacer height={32} />
              <FlexRow className={styles.DrawerSubTitle}>
                {Resource.UPDATE_REASON_HEADER}
              </FlexRow>
              <Spacer height={4} />
              <FlexRow>
                <Typography>{formData.updateReason}</Typography>
              </FlexRow>
              <Spacer height={24} />
            </>
          )}
          {formData.mibCodes?.length && (
            <>
              <FlexRow>
                <FlexCol xs={12}>
                  <span className={styles.DrawerSubTitle}>
                    {Resource.MIB_CODES}
                  </span>
                </FlexCol>
              </FlexRow>
              <Spacer height={4} />
              {formData.mibCodes.map(mibCode => (
                <FlexRow key={uuidv4()}>
                  <span className={styles.Info}>
                    {`# ${mibCode}`}
                    {get(codeback, 'validationFailed', false) ? (
                      <Tooltip
                        title={'Validation failed, you could still submit'}
                      >
                        <SmsFailedRoundedIcon
                          fontSize={'small'}
                          color={'info'}
                          sx={{ marginLeft: '15px', marginBottom: '-5px' }}
                        ></SmsFailedRoundedIcon>
                      </Tooltip>
                    ) : (
                      getValidationResult(mibCode, codeback, resultMap)
                    )}
                  </span>
                </FlexRow>
              ))}
              <Spacer height={24} />
            </>
          )}
          <ReasonsSummary title="POSTPONE REASON" reasons={formData.reasons} />
          <Spacer height={32} />
          <FlexRow>
            <FlexCol xs={12}>
              <span className={styles.DrawerSubTitle}>
                Postpone description
              </span>
            </FlexCol>
          </FlexRow>
          <Spacer height={4} />
          <FlexRow>
            <FlexCol xs={12}>
              <Typography>{formData.postponeDescription}</Typography>
            </FlexCol>
          </FlexRow>
          <Spacer height={32} />
          {!!formData.postponeConditions?.length && (
            <Box>
              <span className={styles.DrawerSubTitle}>
                {Resource.POSTPONE_CONDITIONS}
              </span>
              <ul className={styles.UlList}>
                {formData.postponeConditions.map(name => (
                  <li key={name}>
                    <Typography>{name}</Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}
          {formData.triggerClr === false && (
            <>
              <Spacer height={32} />
              <FlexRow>
                <FlexCol xs={12}>
                  <span className={styles.DrawerSubTitle}>CLR Status</span>
                </FlexCol>
              </FlexRow>
              <Spacer height={4} />
              <FlexRow>
                <FlexCol xs={12}>
                  <Typography color="warning.main">
                    Closed Loop Rescission will NOT be triggered
                  </Typography>
                </FlexCol>
              </FlexRow>
              {(formData.haltClrReasons?.length ?? 0) > 0 && (
                <>
                  <Spacer height={16} />
                  <ReasonsSummary
                    title="CLR HALT REASONS"
                    reasons={formData.haltClrReasons || []}
                  />
                </>
              )}
            </>
          )}
        </FlexGrid>
      </Box>
      <Box>
        <NoraButton
          name={ElementLocators.LOCATOR_SUBMIT_POSTPONE_BUTTON}
          className={styles.DrawerButton}
          onClick={() => {
            // Send CLR halt info from user intent - backend will validate based on lifecycle
            const haltClr = formData.triggerClr === false;
            const haltClrReasons = haltClr
              ? formData.haltClrReasons
              : undefined;

            if (editedDecision) {
              const decisionUpdate = {
                decision: {
                  decision: Decision.POSTPONED,
                  decisionType: editedDecision.decisionType,
                  postponeReasons: formData.reasons,
                  postponeDescription: formData.postponeDescription,
                  postponeConditions: formData.postponeConditions,
                  mibCodes: formData.mibCodes,
                  reviewStatus: editedDecision.reviewStatus,
                  haltClr,
                  haltClrReasons,
                } as IDecisionRequest,
                reason: formData.updateReason,
              } as IDecisionUpdateRequest;
              submitEdit(editedDecision.id.toString(), decisionUpdate, isEditLeadingToCLR);
            } else {
              submitCreate({
                reasons: formData.reasons,
                postponeDescription: formData.postponeDescription,
                postponeConditions: formData.postponeConditions,
                mibCodes: formData.mibCodes,
                triggerClr: formData.triggerClr,
                haltClrReasons: formData.haltClrReasons,
              });
            }
            onBack();
            setIsOpen(false);
          }}
        >
          {Resource.POSTPONE_APPLICATION_HEADER}
        </NoraButton>
      </Box>
    </Drawer>
  );
};
